// WebSocket client for tournament communication

import type {
  TournamentState,
  TournamentPlayer,
  BracketMatch,
  TournamentSize,
  TournamentClientMessage,
  TournamentServerMessage,
} from './tournamentProtocol';

export type {
  TournamentState,
  TournamentPlayer,
  BracketMatch,
  TournamentSize,
};

type EventHandler<T> = (data: T) => void;

interface TournamentEvents {
  tournamentState: { state: TournamentState };
  playerRegistered: { player: TournamentPlayer; playerCount: number; size: TournamentSize };
  playerLeft: { playerId: string; playerCount: number };
  tournamentStarted: { state: TournamentState };
  matchReady: { match: BracketMatch; gameRoomId: string };
  matchResult: { match: BracketMatch; nextMatch?: BracketMatch };
  roundComplete: { round: number; nextRound: number; bracket: BracketMatch[] };
  tournamentComplete: { winnerId: string; winnerName: string; bracket: BracketMatch[] };
  playerTimeout: { playerId: string; matchId: string };
  tournamentChat: { from: string; message: string; timestamp: number };
  tournamentError: { message: string };
  countdown: { secondsRemaining: number; matchId: string };
  connected: void;
  disconnected: void;
}

function getPlayerId(): string {
  const key = 'wizard-duel-player-id';
  let id = localStorage.getItem(key);
  if (!id) {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    id = 'p_';
    for (let i = 0; i < 12; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    localStorage.setItem(key, id);
  }
  return id;
}

function getPlayerName(): string {
  return localStorage.getItem('wizard-duel-player-name') || 'Wizard';
}

function getApiHost(): string {
  const host = window.location.host;
  const isDev = host.includes('localhost') || host.includes('127.0.0.1');
  return isDev ? 'http://localhost:8788' : 'https://wizard-duel-api.neenhouse.workers.dev';
}

export class TournamentClient {
  private ws: WebSocket | null = null;
  private tournamentId: string = '';
  private playerId: string;
  private playerName: string;
  private handlers: Map<keyof TournamentEvents, Set<EventHandler<unknown>>> = new Map();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;

  constructor() {
    this.playerId = getPlayerId();
    this.playerName = getPlayerName();

    const events: (keyof TournamentEvents)[] = [
      'tournamentState', 'playerRegistered', 'playerLeft', 'tournamentStarted',
      'matchReady', 'matchResult', 'roundComplete', 'tournamentComplete',
      'playerTimeout', 'tournamentChat', 'tournamentError', 'countdown',
      'connected', 'disconnected',
    ];
    events.forEach(event => this.handlers.set(event, new Set()));
  }

  async createTournament(name: string, size: TournamentSize): Promise<string> {
    const apiHost = getApiHost();
    const response = await fetch(`${apiHost}/api/tournament`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, size, creatorId: this.playerId }),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create tournament');
    }

    const data = await response.json();
    return data.tournamentId;
  }

  async getTournamentState(tournamentId: string): Promise<TournamentState> {
    const apiHost = getApiHost();
    const response = await fetch(`${apiHost}/api/tournament/${tournamentId}`);
    if (!response.ok) throw new Error('Failed to get tournament state');
    return response.json();
  }

  connect(tournamentId: string): void {
    this.tournamentId = tournamentId;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const isDev = host.includes('localhost') || host.includes('127.0.0.1');
    const workerHost = isDev ? 'localhost:8788' : 'wizard-duel-api.neenhouse.workers.dev';

    const wsUrl = `${protocol}//${workerHost}/ws/tournament/${tournamentId}`;

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.emit('connected', undefined);

      // Auto-join the tournament
      this.send({
        type: 'join_tournament',
        name: this.playerName,
        playerId: this.playerId,
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as TournamentServerMessage;
        this.handleMessage(message);
      } catch (e) {
        console.error('Failed to parse tournament message:', e);
      }
    };

    this.ws.onclose = () => {
      this.emit('disconnected', undefined);
      this.attemptReconnect();
    };

    this.ws.onerror = (error) => {
      console.error('Tournament WebSocket error:', error);
    };
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('tournamentError', { message: 'Connection lost. Please refresh to reconnect.' });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) + Math.random() * 1000,
      30000
    );

    setTimeout(() => {
      if (this.tournamentId) {
        this.connect(this.tournamentId);
      }
    }, delay);
  }

  private handleMessage(message: TournamentServerMessage): void {
    switch (message.type) {
      case 'tournament_state':
        this.emit('tournamentState', { state: message.state });
        break;
      case 'player_registered':
        this.emit('playerRegistered', {
          player: message.player,
          playerCount: message.playerCount,
          size: message.size,
        });
        break;
      case 'player_left':
        this.emit('playerLeft', { playerId: message.playerId, playerCount: message.playerCount });
        break;
      case 'tournament_started':
        this.emit('tournamentStarted', { state: message.state });
        break;
      case 'match_ready':
        this.emit('matchReady', { match: message.match, gameRoomId: message.gameRoomId });
        break;
      case 'match_result':
        this.emit('matchResult', { match: message.match, nextMatch: message.nextMatch });
        break;
      case 'round_complete':
        this.emit('roundComplete', {
          round: message.round,
          nextRound: message.nextRound,
          bracket: message.bracket,
        });
        break;
      case 'tournament_complete':
        this.emit('tournamentComplete', {
          winnerId: message.winnerId,
          winnerName: message.winnerName,
          bracket: message.bracket,
        });
        break;
      case 'player_timeout':
        this.emit('playerTimeout', { playerId: message.playerId, matchId: message.matchId });
        break;
      case 'tournament_chat':
        this.emit('tournamentChat', {
          from: message.from,
          message: message.message,
          timestamp: message.timestamp,
        });
        break;
      case 'tournament_error':
        this.emit('tournamentError', { message: message.message });
        break;
      case 'countdown':
        this.emit('countdown', {
          secondsRemaining: message.secondsRemaining,
          matchId: message.matchId,
        });
        break;
    }
  }

  private send(message: TournamentClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  startTournament(): void {
    this.send({ type: 'start_tournament' });
  }

  leaveTournament(): void {
    this.send({ type: 'leave_tournament' });
  }

  sendChat(message: string): void {
    this.send({ type: 'tournament_chat', message });
  }

  disconnect(): void {
    this.maxReconnectAttempts = 0;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  getPlayerId(): string {
    return this.playerId;
  }

  getTournamentId(): string {
    return this.tournamentId;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  on<K extends keyof TournamentEvents>(
    event: K,
    handler: EventHandler<TournamentEvents[K]>
  ): () => void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.add(handler as EventHandler<unknown>);
    }
    return () => this.off(event, handler);
  }

  off<K extends keyof TournamentEvents>(
    event: K,
    handler: EventHandler<TournamentEvents[K]>
  ): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler<unknown>);
    }
  }

  private emit<K extends keyof TournamentEvents>(
    event: K,
    data: TournamentEvents[K]
  ): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }
}
